import * as THREE from "three";
import Experience from "../Experience";
import { interactedObjects, upcomingConnections, pathwayItems } from "../World/sets";
import type Camera from "../Camera";
import type SceneUtils from "./SceneUtils";
import { debugLog } from "./debugLog";

type RoadObject = THREE.Object3D & { type: string };
export default class PathView {
    experience: Experience;
    scene: THREE.Scene;
    camera: Camera;
    sceneUtils: SceneUtils;
    saveState: boolean;
    index: number;
    timeout: ReturnType<typeof setTimeout> | null;

    constructor() {
        this.experience = new Experience();
        this.scene = this.experience.scene;
        this.camera = this.experience.camera;
        this.sceneUtils = this.experience.sceneUtils;
        this.saveState = false;
        this.index = 0;
        this.timeout = null;
    }

    filterPath(intersect: THREE.Object3D | null) {
        if (intersect === null) {
            this.saveState = true;
            this.scene.children.forEach((object: THREE.Object3D) => {
                object.visible = false;
                this.experience.sceneUtils.showInteractedLines(object);
                this.experience.sceneUtils.showNestedActiveSprites(object);
                this.experience.sceneUtils.showInteractedObjects();
                this.experience.sceneUtils.showRenderedChannels(object);
                this.experience.sceneUtils.showActiveChannels(object);
                this.experience.sceneUtils.showRegenChannels(object);
                this.experience.sceneUtils.showBillboardEls(object);
                this.experience.sceneUtils.showLineToBillboardEls(object);
                this.experience.sceneUtils.showUpcomingConnections(object);
                this.experience.sceneUtils.showLandingEls();
                this.experience.sceneUtils.showCameraLine(object);
                this.experience.sceneUtils.showPathLineGroup();
                if (object?.userData?.regen === true) {
                    object.visible = true;
                }
            });
        } else {
            this.scene.traverse((object: THREE.Object3D) => {
                const isInteracted =
                    interactedObjects.includes(object) ||
                    this.experience.sceneUtils.findRenderedChannels(object) ||
                    object?.userData?.regen === true;
                const isHovered = intersect === object;
                if (isHovered && isInteracted) {
                    this.scene.children.forEach((child: THREE.Object3D) => {
                        child.visible = false;
                        this.experience.sceneUtils.showInteractedLines(child);
                        this.experience.sceneUtils.showNestedActiveSprites(child);
                        this.experience.sceneUtils.showLineToBillboardEls(child);
                        this.experience.sceneUtils.showCameraLine(child);
                        this.experience.sceneUtils.showPathLineGroup();
                    });
                    this.experience.sceneUtils.showInteractedObjects();
                }
                this.experience.sceneUtils.showRenderedChannels(object);
                this.experience.sceneUtils.showActiveChannels(object);
                this.experience.sceneUtils.showBillboardEls(object);
                this.experience.sceneUtils.showUpcomingConnections(object);
            });
        }
    }

    async getPathBoundingBox() {
        const pathBoundingBox = new THREE.Box3();
        await this.experience.sceneOperations.collectVisibleObjectsBoundingBox(pathBoundingBox);
        await this.experience.sceneOperations.fitToBoxWithPadding(pathBoundingBox, "sprite");
    }

    async getUpcomingConnectionsBoundingBox() {
        debugLog(upcomingConnections);
        const upcomingConnectionsBoundingBox = new THREE.Box3();
        if (upcomingConnections.size !== 0) {
            await this.experience.sceneOperations.collectUpcomingConnectionsBoundingBox(upcomingConnectionsBoundingBox);
            await this.experience.sceneOperations.fitToBoxWithPadding(upcomingConnectionsBoundingBox, "sprite");
        }
    }

    removeFilter() {
        this.saveState = false;
        this.scene.traverse((child: THREE.Object3D) => {
            if (
                child.name !== "blockTypeLabel" &&
                child.name !== "connectionCountLabel" &&
                child.name !== "dateLabel" &&
                child.name !== "userLabel" &&
                child.name !== "TitleSprite" &&
                child?.userData?.data?.role !== "Path" &&
                child?.userData?.role !== "PathLine" &&
                child?.name !== "pathLabelLine" &&
                child?.name !== "pathLabelLine" &&
                child?.name !== "pathwayItem" &&
                child?.name !== "TaglineSprite"
            ) {
                if (this.experience.showCamLines) {
                    child.visible = true;
                } else {
                    child.visible = true;
                    this.experience.sceneUtils.hideAllCameraLines();
                }
            }
        });
    }
    async focusRoadObject(targetObject: RoadObject) {
        if (targetObject.type === "Mesh") {
            this.camera.fitToRect(targetObject, this.camera.paddingMesh * 3);
        } else {
            await this.experience.sceneOperations.fitToBoxWithPadding(targetObject, "sprite", 3);
        }
        this.experience.sceneUtils.billboardPlane(targetObject);
    }
    async handleCrossRoadNavigation(
        source: RoadObject[],
        step: 1 | -1,
        boundsOnZero: boolean
    ) {
        if (source.length === 0) {
            return false;
        }
        this.index = (this.index + step + source.length) % source.length;
        if (boundsOnZero && this.index === 0) {
            await this.getPathBoundingBox();
            this.index = this.index % source.length;
            if (this.timeout) {
                this.resetTimeout();
            }
            return true;
        }
        const targetObject = source[this.index];
        if (!targetObject) {
            debugLog("No more interacted objects");
            return true;
        }
        await this.focusRoadObject(targetObject);
        if (this.timeout) {
            this.resetTimeout();
        }
        return true;
    }

    async previousCrossRoad() {
        const interacted = interactedObjects as RoadObject[];
        const pathways = pathwayItems.filter((item): item is RoadObject => Boolean(item)) as RoadObject[];
        if (interacted.length !== 0) {
            await this.handleCrossRoadNavigation(interacted, -1, true);
        } else if (pathways.length !== 0) {
            await this.handleCrossRoadNavigation(pathways, -1, false);
        }
    }

    async nextCrossRoad() {
        const pathways = pathwayItems.filter((item): item is RoadObject => Boolean(item)) as RoadObject[];
        const interacted = interactedObjects as RoadObject[];
        if (pathways.length !== 0) {
            await this.handleCrossRoadNavigation(pathways, 1, false);
        } else if (interacted.length !== 0) {
            await this.handleCrossRoadNavigation(interacted, 1, true);
        }
    }

    resetTimeout() {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
        this.timeout = setTimeout(() => {
            this.nextCrossRoad();
        }, 7000);
    }

    toggleAutoplay(event: MouseEvent & { target: HTMLElement }) {
        const autoplayActive = !this.timeout;
        if (autoplayActive) {
            this.resetTimeout();
            debugLog("Autoplay started", autoplayActive);
            event.target.textContent = "Autoplay";
            event.target.classList.add("active");
        } else {
            if (this.timeout) {
                clearTimeout(this.timeout);
                this.timeout = null;
            }
            debugLog("Autoplay stopped", autoplayActive);
            event.target.textContent = "Autoplay";
            event.target.classList.remove("active");
        }
    }
}
